"use client";

import {
  Children,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface CarouselProps {
  /** Accessible name for the scrolling region. */
  label: string;
  /** Each child becomes one fixed-width, snap-aligned item. */
  children: ReactNode;
}

/**
 * A horizontal snap-scrolling row with previous/next buttons in place of a
 * scrollbar. The row is a focusable region, so arrow keys scroll it, and
 * tabbing into an item scrolls it into view.
 */
export function Carousel({ label, children }: CarouselProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    sync();
    const observer = new ResizeObserver(() => sync());
    observer.observe(el);
    return () => observer.disconnect();
  }, [sync]);

  const page = (direction: 1 | -1) => {
    const el = ref.current;
    el?.scrollBy({
      left: direction * el.clientWidth * 0.9,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative">
      <section
        ref={ref}
        aria-label={label}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling region must be focusable so the keyboard can scroll it
        tabIndex={0}
        onScroll={sync}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth rounded-(--radius-field) pb-1 [scrollbar-width:none] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary) [&::-webkit-scrollbar]:hidden"
      >
        {Children.map(children, (child) => (
          <div className="w-[290px] shrink-0 snap-start">{child}</div>
        ))}
      </section>
      <PageButton direction={-1} hidden={atStart} onClick={() => page(-1)} />
      <PageButton direction={1} hidden={atEnd} onClick={() => page(1)} />
    </div>
  );
}

function PageButton({
  direction,
  hidden,
  onClick,
}: {
  direction: 1 | -1;
  hidden: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={hidden}
      aria-label={direction === 1 ? "Scroll right" : "Scroll left"}
      className={`absolute top-1/2 z-10 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full border border-(--border-subtle) bg-(--bg-white) text-(--fg-secondary) shadow-md transition-opacity hover:border-(--border-strong) hover:text-(--fg-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary) ${
        direction === 1 ? "right-0 translate-x-1/3" : "left-0 -translate-x-1/3"
      } ${hidden ? "pointer-events-none opacity-0" : "opacity-100"}`}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden={true}
      >
        <path d={direction === 1 ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"} />
      </svg>
    </button>
  );
}
